import React from 'react';
import { motion } from 'framer-motion';
import { FiMessageSquare, FiTrendingUp, FiUsers, FiZap, FiBarChart, FiAward, FiCheckCircle } from 'react-icons/fi';

const ThreadsMarketingPage: React.FC = () => {
  const features = [
    {
      icon: FiMessageSquare,
      title: 'Content Creation',
      description: 'We create Threads posts that people actually want to read—no fluff, just value and personality',
    },
    {
      icon: FiTrendingUp,
      title: 'Trend-Based Posting',
      description: 'We jump on trending topics and conversations so your brand stays relevant and visible to your audience',
    },
    {
      icon: FiUsers,
      title: 'Community Engagement',
      description: 'We respond to comments, start conversations, and engage with your audience daily to build real relationships',
    },
    {
      icon: FiZap,
      title: 'Growth & Performance Tracking',
      description: 'We track what\'s working, adjust what\'s not, and grow your Threads presence with real, engaged followers',
    }
  ];

  const whatIsThreadsMarketing = [
    {
      title: 'Meta\'s Text-Based Platform',
      description: 'Threads is Meta\'s Twitter alternative, offering a fresh space for text-based conversations with Instagram integration.',
    },
    {
      title: 'Massive Early Adoption',
      description: 'With millions of users in the first days, Threads offers first-mover advantage for brands getting in early.',
    },
    {
      title: 'Instagram Synergy',
      description: 'Seamless integration with Instagram allows you to leverage your existing Instagram audience on Threads.',
    },
    {
      title: 'Conversation-Focused',
      description: 'Perfect for brands looking to engage in meaningful conversations and build authentic relationships.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '₹10,000',
      period: '/month',
      description: 'Get started on Threads',
      features: [
        '15 posts per month',
        'Profile optimization',
        'Basic content strategy',
        'Engagement monitoring',
        'Monthly performance report',
        'Email support',
      ],
      highlighted: false,
    },
    {
      name: 'Professional',
      price: '₹22,000',
      period: '/month',
      description: 'For active brand presence',
      features: [
        '30 posts per month',
        'Advanced content strategy',
        'Daily engagement',
        'Trend monitoring',
        'Instagram cross-promotion',
        'Weekly analytics reports',
        'Community management',
        'Priority support',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: '₹45,000',
      period: '/month',
      description: 'For comprehensive Threads presence',
      features: [
        'Unlimited posts',
        'Multi-account management',
        'Real-time engagement',
        'Influencer partnerships',
        'Crisis management',
        'Custom content calendar',
        'Advanced analytics dashboard',
        'Dedicated account manager',
        '24/7 support',
      ],
      highlighted: false,
    },
  ];

  const whyChooseUs = [
    {
      title: 'We get in early—before everyone else',
      description: 'Threads is new, and we help you get a head start before the platform gets crowded with competitors',
    },
    {
      title: 'Real conversations, not just posts',
      description: 'We focus on building genuine connections and engaging with your audience in a way that feels human, not robotic',
    },
    {
      title: 'We grow your Threads presence fast',
      description: 'We leverage Instagram integration and smart posting strategies to grow your followers and engagement quickly',
    }
  ];

  const faqs = [
    {
      question: 'What is Threads and how is it different from Twitter/X?',
      answer: 'Threads is Meta\'s text-based conversation app that integrates with Instagram. Unlike Twitter/X, Threads has a cleaner interface, no ads (yet), direct Instagram integration, and a more curated algorithmic feed. It offers a fresh start for brands in the text-based social space.',
    },
    {
      question: 'Do I need an Instagram account to use Threads?',
      answer: 'Yes, Threads requires an Instagram account to sign up. However, this is actually an advantage - it allows you to leverage your existing Instagram followers and easily cross-promote content between platforms.',
    },
    {
      question: 'Is it worth investing in Threads marketing now?',
      answer: 'Absolutely! Early adoption gives you significant advantages - lower competition, better organic reach, and the opportunity to establish authority as the platform grows. Many brands that dominated Instagram got in early; Threads offers a similar opportunity.',
    },
    {
      question: 'How often should I post on Threads?',
      answer: 'Threads rewards consistency and real-time engagement. We typically recommend 3-5 posts per day for optimal visibility, plus active participation in trending conversations. Quality and relevance matter more than volume.',
    },
    {
      question: 'Can I run ads on Threads?',
      answer: 'As of now, Threads doesn\'t have an advertising platform. However, given Meta\'s track record, ads are likely coming. This makes organic growth even more valuable now before the platform becomes pay-to-play.',
    },
    {
      question: 'How do I grow my Threads following?',
      answer: 'Growth comes from consistent posting, engaging with others\' content, joining trending conversations, leveraging your Instagram audience, posting at optimal times, and creating shareable, conversation-starting content. We handle all of this strategically.',
    },
    {
      question: 'What type of content works best on Threads?',
      answer: 'Threads favors authentic, conversational content - behind-the-scenes insights, timely commentary, questions that spark discussion, helpful tips, and content that adds value to conversations. We create content that resonates with Threads\' community-focused culture.',
    },
    {
      question: 'How do you measure success on Threads?',
      answer: 'We track follower growth, engagement rate, reach, profile visits, link clicks, and conversation quality. Since Threads is new, we also monitor how the platform\'s algorithm evolves and adapt strategies accordingly.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100/50 via-transparent to-pink-100/50 z-0" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <FiMessageSquare className="w-4 h-4" />
              Threads Marketing Services
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent leading-tight">
              Threads Marketing
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed break-words">
              Threads Marketing helps your brand connect with people on Meta's newest platform through authentic conversations and engaging content that feels natural, not forced
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300"
              >
                Start Threads Marketing
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-purple-600 px-8 py-4 rounded-xl font-semibold border-2 border-purple-600 hover:bg-purple-600 hover:text-white transition-all duration-300"
              >
                Learn More
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is Threads Marketing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              What is Threads Marketing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Threads marketing is a simple and effective way to reach your ideal audience on Meta's new text-based platform. Think of Threads as Instagram's sister app—but for conversations. As the best Threads marketing agency in India, we help you create posts, join trending topics, and build a loyal community around your brand on a platform that's growing fast and full of opportunity.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatIsThreadsMarketing.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                  {item.title}
                </h3>
                <p className="text-gray-600 break-words">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Our Threads Marketing Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive solutions to establish and grow your Threads presence
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 break-words">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Threads Marketing Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Affordable plans to grow your Threads presence
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className={`relative p-8 rounded-2xl transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-2xl scale-105'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-purple-600'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={plan.highlighted ? 'text-purple-100' : 'text-gray-600'}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-purple-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-purple-600'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-purple-50' : 'text-gray-600'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-white text-purple-600 hover:bg-purple-50'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
                  }`}
                >
                  Get Started
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Why Choose Our Threads Marketing Services?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-purple-600 bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 break-words">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 break-words">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about Threads marketing
            </p>
          </motion.div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl"
              >
                <h3 className="text-lg font-semibold mb-2 text-gray-900 break-words">
                  {faq.question}
                </h3>
                <p className="text-gray-600 break-words">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">
              Ready to Lead on Threads?
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              Get in early and establish your brand as a Threads authority
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-purple-600 px-8 py-4 rounded-xl font-semibold hover:bg-purple-50 transition-all duration-300 shadow-xl"
            >
              Start Today
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ThreadsMarketingPage;
